"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import { AuthModeEnum } from "@pharaday/auth";
import { Button, cn, Separator, TextField, useDebounce } from "@pharaday/lighthouse";
import Link from "next/link";
import { useEffect } from "react";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";

import useLogin from "../../apis/useLogin";
import { useQuerySignInPrecheck } from "../../apis/useQuerySignInPrecheck";
import { useQuerySignInSsoUrl } from "../../apis/useQuerySignInSsoUrl";
import { useRequestMagicLink } from "../../apis/useRequestMagicLink";
import { LoginFormSchema } from "./LoginForm.schema";

export interface StepLoginProps {
  onSuccess: () => void;
  onRequireEmailVerification: (pendingToken: string) => void;
  onRequireEmailOTP: (email: string) => void;
}

const StepLogin = ({ onSuccess, onRequireEmailVerification, onRequireEmailOTP }: StepLoginProps) => {
  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<LoginFormSchema>({
    resolver: yupResolver(LoginFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const email = useWatch({ control, name: "email" });
  const debouncedEmail = useDebounce(email, 500);

  const { mutate: login, data: loginResult, isPending } = useLogin();
  const {
    mutateAsync: requestMagicLink,
    isPending: isRequestingMagicLink,
    isError: isRequestMagicLinkError,
    reset: resetRequestMagicLink,
  } = useRequestMagicLink();
  const { fetch: fetchSignInSsoUrl, isFetching: isFetchingSignInSsoUrl } = useQuerySignInSsoUrl(email);
  const { data: debouncedSignInPrecheck, isFetching: isFetchingSignInPrecheck } =
    useQuerySignInPrecheck(debouncedEmail);

  const signInPrecheck = email === debouncedEmail ? debouncedSignInPrecheck : undefined;

  const signInWithPassword: SubmitHandler<LoginFormSchema> = async (data: LoginFormSchema) =>
    login({
      type: "with-password",
      email: data.email,
      password: data.password,
    });

  const signInWithSso = async () => {
    if (signInPrecheck?.authMode !== AuthModeEnum.SSO) {
      return;
    }

    const data = await fetchSignInSsoUrl();
    if (data?.ssoUrl) {
      globalThis.location.href = data.ssoUrl;
    }
  };

  const signInWithOtp = async () => {
    if (signInPrecheck?.authMode !== AuthModeEnum.OTP) {
      return;
    }

    resetRequestMagicLink();

    try {
      await requestMagicLink(email);
    } catch {
      return;
    }

    onRequireEmailOTP(email);
  };

  useEffect(() => {
    if (!loginResult) {
      return;
    }

    if (loginResult.success) {
      onSuccess();
      return;
    }

    if (loginResult.pendingToken) {
      onRequireEmailVerification(loginResult.pendingToken);
    }
  }, [loginResult]);

  const isLoginResultError = loginResult && !loginResult.success;
  const isLoading = isPending || isFetchingSignInPrecheck || isFetchingSignInSsoUrl || isRequestingMagicLink;

  const shouldLoginWithOtp = signInPrecheck?.authMode === AuthModeEnum.OTP;
  const shouldLoginWithSso = signInPrecheck?.authMode === AuthModeEnum.SSO;
  const shouldLoginWithPassword = signInPrecheck && !shouldLoginWithOtp && !shouldLoginWithSso;
  const buttonLabel = (() => {
    if (shouldLoginWithOtp) {
      return "Login with OTP";
    }
    if (shouldLoginWithSso) {
      return "Login with SSO";
    }
    return "Login";
  })();

  const handleNonPasswordSignIn = (() => {
    if (shouldLoginWithSso) {
      return signInWithSso;
    }
    if (shouldLoginWithOtp) {
      return signInWithOtp;
    }

    return undefined;
  })();

  return (
    <form className="space-y-6" onSubmit={handleSubmit(signInWithPassword)}>
      <div>
        <h1 className="text-2xl">Welcome!</h1>
        <h3 className="text-base font-bold text-neutral-variant-30/60">
          Enter your email and password below to log in
        </h3>
      </div>

      <TextField.Root>
        <TextField.Label>Email</TextField.Label>
        <TextField.Input
          {...register("email", { required: true })}
          placeholder="Enter your registered email"
          isInvalid={!!errors.email?.message || isLoginResultError}
        />
        <TextField.ErrorMessage>{errors.email?.message}</TextField.ErrorMessage>
      </TextField.Root>

      <div
        className={cn(
          "mb-0 h-0 space-y-6 overflow-hidden transition-all duration-300",
          shouldLoginWithPassword && "mb-6 h-unset overflow-visible"
        )}
      >
        <TextField.Root>
          <TextField.Label>Password</TextField.Label>
          <TextField.Password
            {...register("password", { required: true })}
            placeholder="Enter your password"
            isInvalid={!!errors.password?.message || isLoginResultError}
          />
          <TextField.ErrorMessage>{errors.password?.message}</TextField.ErrorMessage>
        </TextField.Root>

        {isLoginResultError && <p className="text-sm text-error-50">Email and password combination is incorrect</p>}

        <p className="flex gap-1 text-sm">
          Forgot your password?{" "}
          <Link href="/request-password-reset">
            <Button color="primary" variant="link" className="underline" type="button">
              Reset it here
            </Button>
          </Link>
        </p>
      </div>

      {shouldLoginWithOtp && isRequestMagicLinkError && (
        <p className="text-sm text-error-50">We couldn&apos;t send a login code. Please try again.</p>
      )}

      <Button
        className="w-full"
        size="lg"
        isLoading={isLoading}
        type={shouldLoginWithPassword ? "submit" : "button"}
        onClick={handleNonPasswordSignIn}
      >
        {buttonLabel}
      </Button>

      <Separator className="mt-4" orientation="horizontal" />

      <p className="flex gap-1 text-sm">
        You don’t have an account yet?{" "}
        <Link href="#">
          <Button color="primary" variant="link" className="underline" type="button">
            Contact us
          </Button>
        </Link>
      </p>
    </form>
  );
};

export default StepLogin;
